import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { RefreshCwIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import {
  deleteDatadogApiCredentials,
  monitoringDatadogQueries,
} from '@/lib/queries/integrations/monitoring/datadog';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

import { getColumns } from './-columns';

export const Route = createFileRoute(
  '/_root/integrations/monitoring/datadog-api-credentials/',
)({
  component: RouteComponent,
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      monitoringDatadogQueries.list(defaultProject.id),
    );

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();
  const { queryClient } = Route.useRouteContext();

  const { data: apiCredentials } = useSuspenseQuery(
    monitoringDatadogQueries.list(defaultProject.id),
  );

  const { toast } = useToast();

  const { mutateAsync: deleteApiCredentials } = useMutation({
    mutationFn: async (id: string) => {
      await deleteDatadogApiCredentials(defaultProject.id, id);
      toast({
        title: 'integrations.monitoring.datadog.list.apiCredentialsDeleted',
      });
    },
    mutationKey: generateMutationKey(
      monitoringDatadogQueries.byProject(defaultProject.id),
      'deleteApiCredentials',
      true,
    ),
  });

  return (
    <>
      <h1 className="text-3xl mb-6">
        <FormattedMessage id="integrations.monitoring.datadog.title" />
      </h1>
      <p className="mb-6">
        <FormattedMessage id="integrations.monitoring.datadog.cta" />
      </p>
      <div className="flex flex-row justify-between items-center mt-8">
        <div className="flex flex-row items-baseline">
          <h2 className="text-2xl">
            <FormattedMessage id="integrations.monitoring.datadog.list.title" />
          </h2>
          <Button
            variant="ghost"
            className="ml-2 h-8 w-8 p-0"
            onClick={() => {
              queryClient.invalidateQueries(
                monitoringDatadogQueries.list(defaultProject.id),
              );
            }}
          >
            <RefreshCwIcon className="h-4 w-4" />
          </Button>
        </div>
        <TooltipProvider disableHoverableContent>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <Link
                to="/integrations/monitoring/datadog-api-credentials/new"
                className="text-blue-500 hover:text-blue-700"
              >
                <Button variant="main" disabled={apiCredentials.length > 0}>
                  <FormattedMessage id="integrations.monitoring.datadog.list.new" />
                </Button>
              </Link>
            </TooltipTrigger>
            {apiCredentials.length > 0 && (
              <TooltipContent side="bottom">
                <p className="text-xs">
                  <FormattedMessage id="integrations.monitoring.datadog.list.newDisabled" />
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
      <DataTable
        columns={getColumns(deleteApiCredentials)}
        data={apiCredentials}
        headersTranslationPath="integrations.monitoring.datadog.list.table"
      />
    </>
  );
}
